import clarity from '@microsoft/clarity';

/**
 * Microsoft Clarity analytics service
 * Provides session recordings, heatmaps, and user insights
 */

class ClarityService {
    private initialized = false;
    private projectId: string | null = null;
    private hasAnalyticsConsent = false;

    /**
     * Update consent for Clarity tracking
     * @param analyticsConsent - Whether user consented to analytics
     */
    updateConsent(analyticsConsent: boolean): void {
        this.hasAnalyticsConsent = analyticsConsent;

        if (typeof window === 'undefined') {
            return;
        }

        // If consent is granted and not initialized, initialize now
        if (analyticsConsent && !this.initialized && this.projectId) {
            this.initializeClarity(this.projectId);
        }

        // Update consent if already initialized
        if (this.initialized) {
            try {
                clarity.consent(analyticsConsent);
            } catch (error) {
                console.error('[ClarityService] Failed to update consent:', error);
            }
        }
    }

    /**
     * Initialize Clarity analytics
     * Call this once during app initialization
     * @param projectId - Your Clarity project ID from https://clarity.microsoft.com
     */
    initialize(projectId?: string): void {
        // Only initialize in browser environment
        if (typeof window === 'undefined') {
            return;
        }

        // Don't initialize if already initialized
        if (this.initialized) {
            console.warn('[ClarityService] Clarity is already initialized');
            return;
        }

        // Get project ID from parameter or environment variable
        const clarityProjectId = projectId || import.meta.env.VITE_CLARITY_PROJECT_ID;

        if (!clarityProjectId) {
            if (import.meta.env.DEV) {
                console.warn(
                    '[ClarityService] Clarity project ID not found. Set VITE_CLARITY_PROJECT_ID environment variable or pass projectId to initialize()'
                );
            }
            return;
        }

        this.projectId = clarityProjectId;

        // Only initialize if we have consent
        // Otherwise, wait for consent update
        if (this.hasAnalyticsConsent) {
            this.initializeClarity(clarityProjectId);
        } else {
            console.info('[ClarityService] Clarity will initialize after consent is granted');
        }
    }

    /**
     * Internal method to initialize Clarity
     */
    private initializeClarity(projectId: string): void {
        if (this.initialized || typeof window === 'undefined') {
            return;
        }

        try {
            clarity.init(projectId);
            this.initialized = true;
            console.info('[ClarityService] Clarity initialized successfully');
        } catch (error) {
            // Silently handle initialization errors (e.g., network failures, CORS issues)
            // These are non-critical and shouldn't break the app
            if (import.meta.env.DEV) {
                console.warn('[ClarityService] Failed to initialize Clarity:', error);
            }
            // Don't set initialized to true on error, so we can retry later if needed
        }

        // Monitor for script loading errors
        if (typeof window !== 'undefined') {
            window.addEventListener('error', (event) => {
                if (event.target instanceof HTMLScriptElement && 
                    event.target.src?.includes('clarity.ms')) {
                    // Silently handle Clarity script loading errors
                    if (import.meta.env.DEV) {
                        console.warn('[ClarityService] Clarity script failed to load:', event.target.src);
                    }
                    event.preventDefault(); // Prevent error from bubbling
                }
            }, { once: true, passive: true });
        }
    }

    /**
     * Check if Clarity is initialized
     */
    isInitialized(): boolean {
        return this.initialized;
    }

    /**
     * Identify a user for Clarity analytics
     * @param customerId - Unique user identifier (required)
     * @param customSessionId - Optional session identifier
     * @param customPageId - Optional page identifier
     * @param friendlyName - Optional friendly name for the user
     */
    identify(customerId: string, customSessionId?: string, customPageId?: string, friendlyName?: string): void {
        if (!this.hasAnalyticsConsent || !this.initialized || typeof window === 'undefined') {
            return;
        }

        try {
            clarity.identify(customerId, customSessionId, customPageId, friendlyName);
        } catch (error) {
            console.error('[ClarityService] Failed to identify user:', error);
        }
    }

    /**
     * Set custom tags for Clarity sessions
     * @param key - Tag key
     * @param value - Tag value (string or array of strings)
     */
    setTag(key: string, value: string | string[]): void {
        if (!this.hasAnalyticsConsent || !this.initialized || typeof window === 'undefined') {
            return;
        }

        try {
            clarity.setTag(key, value);
        } catch (error) {
            console.error('[ClarityService] Failed to set tag:', error);
        }
    }

    /**
     * Upgrade the current session to prioritize recording
     * @param reason - Reason for upgrading the session
     */
    upgrade(reason: string): void {
        if (!this.hasAnalyticsConsent || !this.initialized || typeof window === 'undefined') {
            return;
        }

        try {
            clarity.upgrade(reason);
        } catch (error) {
            console.error('[ClarityService] Failed to upgrade session:', error);
        }
    }

    /**
     * Track a custom event in Clarity
     * @param eventName - Name of the event to track
     */
    event(eventName: string): void {
        if (!this.hasAnalyticsConsent || !this.initialized || typeof window === 'undefined') {
            return;
        }

        try {
            clarity.event(eventName);
        } catch (error) {
            console.error('[ClarityService] Failed to track event:', error);
        }
    }
}

// Export singleton instance
export const clarityService = new ClarityService();

